import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/jamie-jones-paradise-unvrs-ibiza-24-june-2026";
const IMAGE_URL = "https://www.hey-lets-travel.com/assets/community/photo_2026-06-24_16-06-28.jpg";
const TICKET_URL = "https://www.unvrs.com/event/jamie-jones-paradise/24-06-2026";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const MAIN_ROOM_LINEUP = ["Jamie Jones B2B The Martinez Brothers", "Cloonee", "Richy Ahmed", "Joella Jackson"];
const WILD_COMET_LINEUP = ["Jean Pierre B2B Goosey", "AJ Christou", "Volkoder", "Dale Hart"];
const ALL_ARTISTS = [...MAIN_ROOM_LINEUP, ...WILD_COMET_LINEUP];

const KEYWORDS = [
  "Jamie Jones Paradise UNVRS Ibiza 24 June 2026",
  "Jamie Jones B2B The Martinez Brothers UNVRS",
  "Paradise UNVRS Ibiza tickets",
  "UNVRS Ibiza 24 June 2026 tickets",
  "Cloonee UNVRS Ibiza 2026",
  "Richy Ahmed UNVRS Ibiza",
  "Joella Jackson Ibiza 2026",
  "Jean Pierre B2B Goosey Wild Comet",
  "AJ Christou UNVRS Ibiza",
  "Volkoder UNVRS Ibiza",
  "Jamie Jones Ibiza 2026",
  "The Martinez Brothers Ibiza 2026",
  "Ibiza house music tonight",
  "San Rafael Ibiza club tickets",
  "UNVRS Ibiza Wednesday events",
  "Ibiza tickets and concierge",
  "HLT Ibiza concierge",
  "Ibiza hotels near UNVRS",
];

const FAQS = [
  {
    question: "When is Jamie Jones Presents Paradise at UNVRS Ibiza?",
    answer: "Jamie Jones Presents Paradise is at UNVRS Ibiza on Wednesday 24 June 2026, with doors listed from 23:30.",
  },
  {
    question: "Who is playing Paradise at UNVRS on 24 June 2026?",
    answer: "The Main Room lineup is Jamie Jones B2B The Martinez Brothers, Cloonee, Richy Ahmed and Joella Jackson. Wild Comet features Jean Pierre B2B Goosey, AJ Christou, Volkoder and Dale Hart.",
  },
  {
    question: "How much are tickets for Jamie Jones Presents Paradise at UNVRS?",
    answer: "UNVRS lists Entry Before 1am from 80 EUR and Standard Ticket from 90 EUR for the 24 June 2026 Paradise date. Always check the official UNVRS ticket page for live availability.",
  },
  {
    question: "Where is UNVRS Ibiza?",
    answer: "UNVRS is in San Rafael, Ibiza, on the former Privilege site. It is best reached by taxi, transfer or pre-arranged transport from Ibiza Town, Playa d'en Bossa, San Antonio or nearby hotels.",
  },
  {
    question: "Can HLT help with the full Paradise at UNVRS trip?",
    answer: "Yes. HLT can help with ticket direction, hotel options, villas, transfers, VIP extras and concierge support around Paradise at UNVRS Ibiza.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Jamie Jones Presents Paradise at UNVRS Ibiza 24 June 2026: Tickets, Lineup and HLT Guide",
  description:
    "Jamie Jones Presents Paradise at UNVRS Ibiza on Wednesday 24 June 2026 with Jamie Jones B2B The Martinez Brothers, Cloonee, Richy Ahmed, Joella Jackson and Wild Comet guests.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-06-24",
  dateModified: "2026-06-24",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "Jamie Jones Presents Paradise at UNVRS Ibiza",
  startDate: "2026-06-24T23:30:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "UNVRS Ibiza",
    address: "San Rafael, Ibiza, Spain",
  },
  performer: ALL_ARTISTS.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: TICKET_URL,
    priceCurrency: "EUR",
    price: "80",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "Buy Paradise Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Build the Trip With HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function JamieJonesParadiseUnvrsArticle() {
  return (
    <>
      <Helmet>
        <title>Jamie Jones Paradise UNVRS Ibiza 24 June 2026 Tickets | HLT Guide</title>
        <meta
          name="description"
          content="Jamie Jones Presents Paradise at UNVRS Ibiza on Wednesday 24 June 2026 with Jamie Jones B2B The Martinez Brothers, Cloonee, Richy Ahmed and Wild Comet guests. Official ticket link, hotels and HLT concierge."
        />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Jamie Jones Paradise UNVRS Ibiza 24 June 2026 Tickets" />
        <meta property="og:description" content="A music-first HLT guide to Paradise at UNVRS Ibiza with Jamie Jones B2B The Martinez Brothers, Cloonee and Richy Ahmed." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Jamie Jones Paradise UNVRS Ibiza 24 June 2026 Tickets" />
        <meta name="twitter:description" content="Official ticket link, lineup, room guide and HLT concierge planning for Paradise at UNVRS Ibiza." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">HLT Essential · Ibiza Tonight</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              Jamie Jones Presents Paradise at UNVRS Ibiza: 24 June 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Paradise lands at UNVRS Ibiza tonight with Jamie Jones B2B The Martinez Brothers in the Main Room, plus Cloonee, Richy Ahmed and Joella Jackson. Wild Comet adds Jean Pierre B2B Goosey, AJ Christou, Volkoder and Dale Hart for a full-house Wednesday built around serious house music.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta children="Buy official UNVRS tickets" />
              <ConciergeCta children="Plan UNVRS with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>Jamie Jones Presents Paradise</dd></div>
              <div><dt className="text-white">Venue</dt><dd>UNVRS Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Wednesday 24 June 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>23:30</dd></div>
              <div><dt className="text-white">Tickets</dt><dd>Entry Before 1am from 80 EUR · Standard Ticket from 90 EUR</dd></div>
              <div><dt className="text-white">Sound</dt><dd>Paradise house, tech house, deep groove and big-room Ibiza pressure</dd></div>
            </dl>
          </aside>
        </header>

        <figure className="overflow-hidden border border-brand-500/20 bg-black">
          <img src="/assets/community/photo_2026-06-24_16-06-28.jpg" alt="Jamie Jones Presents Paradise at UNVRS Ibiza 24 June 2026 artwork" className="w-full object-cover" />
        </figure>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr),20rem]">
          <div className="space-y-8">
            <ArticleSection title="Why tonight matters">
              <p>
                This is one of the sharpest Ibiza listings tonight because it combines a heavyweight Paradise headline moment with the scale of UNVRS. Jamie Jones B2B The Martinez Brothers is not filler billing. It is the kind of back-to-back that brings together Paradise's rolling, hypnotic house language with the New York-rooted swagger and percussion-led pressure The Martinez Brothers are known for.
              </p>
              <p>
                UNVRS lists the 24 June 2026 date from 23:30, with Entry Before 1am tickets from 80 EUR and Standard Ticket from 90 EUR. For anyone already on the island, that makes this a clear late-night decision: book direct, sort transport early, and plan the night around a long Main Room run rather than treating it as a casual stop-in.
              </p>
              <p>
                The poster says it plainly: Main Room firepower, Wild Comet depth, and a venue built to make a Wednesday feel huge. If you are searching for Ibiza house music tonight, this is the event to have on the shortlist.
              </p>
            </ArticleSection>

            <ArticleSection title="Main Room: Jamie Jones B2B The Martinez Brothers">
              <p>
                The Main Room is led by Jamie Jones B2B The Martinez Brothers, a pairing that makes sense for Paradise at UNVRS. Jamie Jones brings the Paradise identity: warm basslines, controlled tension, playful vocal moments and the kind of groove that can hold a room for hours. The Martinez Brothers bring a sharper, more percussive edge, with the confidence to move between house, techno swing and stripped-back club tools without losing the crowd.
              </p>
              <p>
                In a venue like UNVRS, that balance matters. A huge room can flatten subtle music if the programming is weak. This lineup should do the opposite: keep the groove muscular enough for the room while still giving Paradise fans the rolling, music-led feeling they came for.
              </p>
              <p>
                Cloonee adds another peak-time layer. His sets usually lean into direct tech-house impact: heavy bass pressure, catchy hooks and big-room release points. Richy Ahmed brings deeper UK house credibility, while Joella Jackson gives the bill a more current, selector-led finish. Together, the Main Room reads like a proper Wednesday headline package rather than a single-name poster.
              </p>
            </ArticleSection>

            <ArticleSection title="Wild Comet: Jean Pierre, Goosey, AJ Christou, Volkoder and Dale Hart">
              <p>
                Wild Comet is a key part of the UNVRS experience, and tonight it gives Paradise a second room with its own identity. Jean Pierre B2B Goosey should sit in a tougher, groove-focused pocket: rolling drums, New York and UK influences, and the kind of compact pressure that works when people want a room change without losing intensity.
              </p>
              <p>
                AJ Christou fits naturally into the Paradise orbit. His sound sits close to refined house and tech-house with enough polish for a major Ibiza room. Volkoder brings a Brazilian club edge, often more direct and bassline-driven, while Dale Hart rounds out the Wild Comet programming for people who want to move through the night rather than stay planted in the Main Room.
              </p>
              <p>
                The best way to approach this event is not to treat Wild Comet as a side room. Use it properly. Start in the Main Room for the headline energy, then step into Wild Comet when you want a tighter, sweatier pocket of house music.
              </p>
            </ArticleSection>

            <ArticleSection title="Ticket link and timing">
              <p>
                For this article, we are linking directly to the official UNVRS event page for Jamie Jones Presents Paradise on 24 June 2026. That page is the cleanest place to check live ticket availability, current pricing and any final door or entry-condition updates before you travel to the venue.
              </p>
              <p>
                The listed ticket tiers are Entry Before 1am from 80 EUR and Standard Ticket from 90 EUR. If you are going tonight, do not leave this late. UNVRS is outside the main resort strips, so the real plan is ticket first, then taxi or transfer, then arrival time.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy tickets on UNVRS" />
                <ConciergeCta children="Ask HLT to arrange the night" />
              </div>
            </ArticleSection>

            <ArticleSection title="Where to stay for UNVRS">
              <p>
                UNVRS is in San Rafael, so the best base depends on the type of trip you want. Ibiza Town is strong if you want restaurants, marina energy and a polished pre-event dinner before heading across the island. Playa d'en Bossa works if your wider trip includes Hï Ibiza, Ushuaïa and beach-club days. San Antonio can work for groups watching budget and sunset plans, but transport needs to be planned properly.
              </p>
              <p>
                For a luxury black-and-gold HLT trip, the cleanest setup is simple: hotel or villa first, dinner or pre-drinks second, pre-booked transport third, ticket link locked. That stops the night becoming a taxi scramble after midnight.
              </p>
              <p>
                HLT can help with the wider route: hotels near Ibiza Town or Playa d'en Bossa, villa options, transfers, tables and concierge planning around Paradise at UNVRS.
              </p>
            </ArticleSection>

            <ArticleSection title="What to listen to before you go">
              <p>
                Start with Jamie Jones and Paradise mixes if you want the mood: rolling bass, warm house detail and a patient build. Then move into The Martinez Brothers for the percussive, New York-centred energy that should make the back-to-back feel bigger than a normal headline set.
              </p>
              <p>
                For Cloonee, listen for modern tech-house weapons and bass-heavy club tracks. Richy Ahmed is the deeper counterweight, especially if you want the UK house thread that runs through so much of Ibiza's underground programming. For the Wild Comet room, check Jean Pierre, Goosey, AJ Christou and Volkoder through recent mixes rather than only top tracks; the room will be about flow as much as individual records.
              </p>
            </ArticleSection>

            <ArticleSection title="Event details at a glance">
              <ul className="grid gap-2 text-slate-300">
                <li><strong className="text-white">Event:</strong> Jamie Jones Presents Paradise</li>
                <li><strong className="text-white">Venue:</strong> UNVRS Ibiza, San Rafael</li>
                <li><strong className="text-white">Date:</strong> Wednesday 24 June 2026</li>
                <li><strong className="text-white">Time:</strong> 23:30</li>
                <li><strong className="text-white">Main Room:</strong> Jamie Jones B2B The Martinez Brothers, Cloonee, Richy Ahmed, Joella Jackson</li>
                <li><strong className="text-white">Wild Comet:</strong> Jean Pierre B2B Goosey, AJ Christou, Volkoder, Dale Hart</li>
                <li><strong className="text-white">Tickets:</strong> Entry Before 1am from 80 EUR · Standard Ticket from 90 EUR</li>
                <li><strong className="text-white">Official ticket link:</strong> <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="text-brand-500 underline underline-offset-4">UNVRS event tickets</a></li>
              </ul>
            </ArticleSection>

            <ArticleSection title="Final word">
              <p>
                Paradise at UNVRS tonight is built for people who want Ibiza house music at full scale: Jamie Jones, The Martinez Brothers, Cloonee, Richy Ahmed and a Wild Comet room that keeps the night moving. It has the urgency of a same-day ticket decision, but the lineup is strong enough to justify building the whole evening around it.
              </p>
              <p>
                Book the ticket through the official UNVRS event page, plan the transport before you start the night, and use HLT if you want the rest of the trip shaped around the music rather than patched together afterwards.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy Jamie Jones Paradise tickets" />
                <Link to={EVENTS_URL} className="btn-secondary">Browse Ibiza events -&gt;</Link>
              </div>
            </ArticleSection>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-500/20 bg-[#111111] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">HLT trip builder</p>
              <h3 className="mt-3 font-display text-3xl leading-none text-white">Lock the ticket, then build the night.</h3>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Need a hotel, villa, transfer, table or group itinerary around Paradise at UNVRS? Send the request and HLT can shape the night around the event.
              </p>
              <div className="mt-4 grid gap-3">
                <TicketCta children="Official tickets" />
                <ConciergeCta children="Concierge request" />
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Good to know</p>
              <ul className="mt-3 grid gap-3 text-sm leading-6 text-slate-300">
                <li><strong className="text-white">Arrive early:</strong> the lower ticket tier is listed for entry before 1am.</li>
                <li><strong className="text-white">Book direct:</strong> use the official UNVRS page for live ticket availability.</li>
                <li><strong className="text-white">Plan transport:</strong> UNVRS is in San Rafael, so pre-arranged taxis or transfers are worth it.</li>
              </ul>
            </div>
          </aside>
        </div>

        <section className="space-y-4 border-t border-white/10 pt-8">
          <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
          <div className="grid gap-3">
            {FAQS.map((item) => (
              <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                <p className="mt-3 text-sm leading-6 text-slate-300">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </article>
    </>
  );
}
